import React, { memo } from 'react';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';

interface TablePaginationComponentProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

const TablePaginationComponent: React.FC<TablePaginationComponentProps> = ({
    count,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
}) => {
    const handleFirstPage = () => {
        onPageChange(null, 0);
    };

    const handlePreviousPage = () => {
        if (page > 0) {
            onPageChange(null, page - 1);
        }
    };

    const handleNextPage = () => {
        if (page < Math.ceil(count / rowsPerPage) - 1) {
            onPageChange(null, page + 1);
        }
    };

    const handleLastPage = () => {
        const lastPage = Math.ceil(count / rowsPerPage) - 1;
        onPageChange(null, lastPage);
    };

    const ActionsComponent = memo(() => (
        <div className="flex items-center gap-1.5">
            <button
                onClick={handleFirstPage}
                disabled={page === 0}
                className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all ${
                    page === 0 
                        ? 'text-slate-300 bg-slate-50 cursor-not-allowed border border-slate-100' 
                        : 'text-slate-600 bg-white hover:bg-slate-50 hover:text-slate-900 border border-slate-200 shadow-sm active:scale-[0.95]'
                }`}
                aria-label="First Page"
            >
                <FirstPageIcon sx={{ fontSize: '18px' }} />
            </button>
            <button
                onClick={handlePreviousPage}
                disabled={page === 0}
                className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all ${
                    page === 0 
                        ? 'text-slate-300 bg-slate-50 cursor-not-allowed border border-slate-100' 
                        : 'text-slate-600 bg-white hover:bg-slate-50 hover:text-slate-900 border border-slate-200 shadow-sm active:scale-[0.95]'
                }`}
                aria-label="Previous Page"
            >
                <NavigateBeforeIcon sx={{ fontSize: '18px' }} />
            </button>

            {/* Page info displayed between buttons */}
            <span className="mx-2 text-xs font-black text-slate-500 uppercase tracking-widest bg-slate-100/80 px-3 py-1.5 rounded-xl border border-slate-200/40">
                PAGE {page + 1} / {Math.ceil(count / rowsPerPage) || 1}
            </span>

            <button
                onClick={handleNextPage}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all ${
                    page >= Math.ceil(count / rowsPerPage) - 1 
                        ? 'text-slate-300 bg-slate-50 cursor-not-allowed border border-slate-100' 
                        : 'text-slate-600 bg-white hover:bg-slate-50 hover:text-slate-900 border border-slate-200 shadow-sm active:scale-[0.95]'
                }`}
                aria-label="Next Page"
            >
                <NavigateNextIcon sx={{ fontSize: '18px' }} />
            </button>
            <button
                onClick={handleLastPage}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all ${
                    page >= Math.ceil(count / rowsPerPage) - 1 
                        ? 'text-slate-300 bg-slate-50 cursor-not-allowed border border-slate-100' 
                        : 'text-slate-600 bg-white hover:bg-slate-50 hover:text-slate-900 border border-slate-200 shadow-sm active:scale-[0.95]'
                }`}
                aria-label="Last Page"
            >
                <LastPageIcon sx={{ fontSize: '18px' }} />
            </button>
        </div>
    ));

    return (
        <div className="flex items-center justify-between p-5 bg-slate-50/60 border border-t-0 border-slate-100 w-full rounded-b-[2rem] gap-4 flex-wrap mt-0">
            <div className="flex items-center gap-3">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Records</span>
                <span className="px-2.5 py-1 bg-slate-200/50 text-slate-700 font-black text-xs rounded-xl border border-slate-200/30">
                    {count}
                </span>
            </div>
            
            <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Items Per Page</span>
                    <div className="relative inline-flex items-center">
                        <select
                            value={rowsPerPage}
                            onChange={onRowsPerPageChange}
                            className="appearance-none pl-3.5 pr-8 py-1.5 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100 transition-all cursor-pointer shadow-sm"
                        >
                            {[5, 10, 15, 20].map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                        <div className="absolute right-2.5 pointer-events-none text-slate-400">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                </div>

                <ActionsComponent />
            </div>
        </div>
    );
};

export default TablePaginationComponent;
